import { useMutation } from '@apollo/client';
import type { Community, OptionalMetadata, SecurityGroupsInput } from '@communities/community/domain';
import { GET_COMMUNITY } from '@communities/community/graphql';
import { UPDATE_COMMUNITY } from '@communities/community/graphql/updateCommunity';
import { GENERATE_COMMUNITY_PROMPTS, GET_COMMUNITY_PROMPTS } from '@communities/prompt-management/graphql';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface UpdateCommunityInput {
  name?: string;
  optionalMetadata?: OptionalMetadata;
  securityGroups?: SecurityGroupsInput;
}

export interface UpdateCommunityResponse {
  community: Community;
}

export function useUpdateCommunity() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  // Community update mutation
  const [updateCommunityMutation, { loading: isUpdating }] = useMutation(UPDATE_COMMUNITY);

  // Prompt generation mutation
  const [generatePromptsMutation, { loading: isGenerating }] = useMutation(GENERATE_COMMUNITY_PROMPTS);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  // Helper function to check if the update is only for security groups
  const isSecurityGroupsOnlyUpdate = (input: UpdateCommunityInput): boolean =>
    // If securityGroups is the only property in the input, it's a security groups only update
    !!input.securityGroups && !input.name && !input.optionalMetadata;

  const updateCommunity = useCallback(
    async (id: string, input: UpdateCommunityInput): Promise<boolean> => {
      try {
        track('UpdateCommunityInitiated', {
          communityId: id,
        });

        // Step 1: Update the community
        await updateCommunityMutation({
          variables: { id, input },
          refetchQueries: id ? [{ query: GET_COMMUNITY, variables: { id } }] : [],
          awaitRefetchQueries: true,
        });

        // Check if this is a security groups only update
        if (!isSecurityGroupsOnlyUpdate(input)) {
          // Step 2: Generate prompts
          await generatePromptsMutation({
            variables: {
              communityId: id,
              input: { labelFeedback: [] },
            },
            refetchQueries: [{ query: GET_COMMUNITY_PROMPTS, variables: { communityId: id } }],
            awaitRefetchQueries: true,
            errorPolicy: 'ignore',
          });
        }

        track('UpdateCommunitySucceeded', {
          communityId: id,
        });

        return true;
      } catch (error) {
        track('UpdateCommunityFailed', {
          communityId: id,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        setErrors(transformErrors(error));

        return false;
      }
    },
    [updateCommunityMutation, generatePromptsMutation]
  );

  return {
    updateCommunity,
    isUpdating,
    isGenerating,
    errors,
    resetErrors,
  };
}

export default useUpdateCommunity;
